import React, { useContext, useState, useRef } from "react";

import {
  Badge,
  Button,
  Image,
  Space,
  Table,
  Tag,
  Tooltip,
  Statistic,
  Row,
  Col,
  Typography,
} from "antd";
import { capitalizeFirstLetter, formatToINR } from "../../../helpers/utility";
import { getTablePagination } from "../../../utils/tablePagination";
import useReceptionListTableScroll from "../../../hooks/useReceptionListTableScroll";
import useIsMobile from "../../../hooks/useIsMobile";
import InPatientModal from "../Inpatients/InPatientModal";
import AddPatientModal from "../AddPatientModal";
import AppointsModal from "../Appointments/AppointsModal";
import ipIcon from "../../../assets/receptionistIcons/ipIcon.svg";
import appointmentIcon from "../../../assets/receptionistIcons/appointmentIcon.svg";
import btnStyles from "../../../styles/Custom/Button.module.css";
import bedIcon from "../../../assets/images/patientIcons/bed.svg";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import { InfoCircleOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import ActionIconButton from "../../ui/ActionIconButton";
import { buildPatientIpUrl } from "../../../helpers/urlFilters";

const AllPatientTable = ({ data, refresh, setPage, page, loading }) => {
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = useReceptionListTableScroll(tableWrapRef, isMobile);
  const navigate = useNavigate();
  const [editPatientModal, setEditPatientModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [addAppointment, setAddAppointment] = useState(false);
  const { userDetails } = useContext(UserContext);
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const selectedFacility =
    Number(getCookie(FacilityID)) || userDetails?.currFacility;

  const activeRoleObj = userDetails?.activeOrg?.roles?.find(
    (r) => r.role === defaultRole && r.facility_id === Number(selectedFacility)
  );

  const ipAvailable = Boolean(activeRoleObj?.ip_available);
  const opAvailable = Boolean(activeRoleObj?.op_available);

  const wrapActionTooltip = (title, children) =>
    isMobile ? (
      children
    ) : (
      <Tooltip title={title} trigger={["hover"]}>{children}</Tooltip>
    );

  const customImageIcon = (icon) => {
    return (
      <Image src={icon} alt="ipIcon" width={20} height={20} preview={false} />
    );
  };

  const columns = [
    {
      title: "Patient ID",
      key: "patient_id",
      dataIndex: "patient_id",
      width: 120,
    },
    { title: "Phone Number", key: "mobile", dataIndex: "mobile", width: 120 },
    {
      title: "Full Name",
      width: 380,
      render: (_, record) => (
        <Row>
          <Col span={12}>
            {capitalizeFirstLetter(record?.first_name) +
              " " +
              capitalizeFirstLetter(record?.last_name)}
          </Col>
          <Col span={6}>
            <Tag>
              {(() => {
                const age = record?.age_by_dob;
                const gender = record?.gender;
                const genderShort =
                  gender === "Male"
                    ? "M"
                    : gender === "Female"
                    ? "F"
                    : gender === "Other"
                    ? "Other"
                    : "";
                let ageText = "";
                if (age != null) {
                  if (age < 1) {
                    const months = Math.round(age * 12);
                    ageText = `${months} months`;
                  } else {
                    ageText = `${Math.floor(age)}`;
                  }
                }
                if (ageText && genderShort)
                  return `${ageText} / ${genderShort}`;
                if (ageText) return ageText;
                if (genderShort) return genderShort;
                return "";
              })()}
            </Tag>
            {/* <Tag>
              {`${record?.age ? record?.age : ""} ${
                record?.gender === "Male"
                  ? "M"
                  : record?.gender === "Female"
                  ? "F"
                  : "Others"
              }`}
            </Tag> */}
          </Col>
          <Col span={6}>
            {record?.is_inpatient && (
              <>
                {record?.ipadmission__bed__bed_no && (
                  <Tag color="orange">
                    <Image src={bedIcon} preview={false} />{" "}
                    {record?.ipadmission__bed__bed_no}
                  </Tag>
                )}
              </>
            )}
          </Col>
        </Row>
      ),
    },
    {
      title: "Notes",
      key: "notes",
      dataIndex: "notes",
      width: 200,
      render: (value) => (value ? <Typography>{value}</Typography> : "-"),
    },
    {
      title: (
        <>
          Balance{" "}
          <>
            <Tooltip
              title="Balances refresh automatically every hour.
Use “Sync Balance” to update the amount instantly."
            >
              <InfoCircleOutlined />
            </Tooltip>
          </>
        </>
      ),
      key: "balance",
      dataIndex: "balance",
      width: 150,
      render: (value, record) =>
        value * -1 < 0 ? (
          <Statistic
            value={formatToINR(value * -1)}
            // prefix="-"
            valueStyle={{ color: "#FD6050", fontSize: "16px" }}
          />
        ) : value * -1 > 0 ? (
          <Statistic
            value={formatToINR(value * -1)}
            prefix="+"
            valueStyle={{ color: "#40C164", fontSize: "16px" }}
          />
        ) : (
          <Statistic
            value={formatToINR(value)}
            valueStyle={{ color: "#40C164", fontSize: "16px" }}
          />
        ),
    },
    {
      title: "Action",
      width: 250,
      render: (_, record) => (
        <>
          {record?.is_inpatient && ipAvailable ? (
            <Space>
              <Button
                type="primary"
                ghost
                className={btnStyles.smBtn}
                onClick={() =>
                  navigate(
                    buildPatientIpUrl(
                      `/receptionist/home?patient=${record?.ipadmission}&type=billing`
                    )
                  )
                }
              >
                IP Billing
              </Button>
              {wrapActionTooltip(
                "Edit Patient",
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <ActionIconButton
                    aria-label="Edit Patient"
                    onClick={() => {
                      setEditPatientModal(true);
                      setEditData(record);
                    }}
                  >
                    <EditIcon />
                  </ActionIconButton>
                </span>
              )}
            </Space>
          ) : (
            <Space>
              {ipAvailable &&
                wrapActionTooltip(
                  "IP Admission",
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    <ActionIconButton
                      aria-label="IP Admission"
                      onClick={() => {
                        setOpen(true);
                        setEditData(record);
                      }}
                    >
                      {customImageIcon(ipIcon)}
                      {record?.is_inpatient && (
                        <Badge
                          color="green"
                          status="processing"
                          className="custom-badge-size"
                          style={{
                            position: "absolute",
                            top: -5,
                            right: 3,
                          }}
                        />
                      )}
                    </ActionIconButton>
                  </span>
                )}
              {opAvailable &&
                wrapActionTooltip(
                  "Book Appointment",
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <ActionIconButton
                      aria-label="Book Appointment"
                      onClick={() => {
                        setAddAppointment(true);
                        setEditData(record);
                      }}
                    >
                      {customImageIcon(appointmentIcon)}
                    </ActionIconButton>
                  </span>
                )}

              {wrapActionTooltip(
                "Edit Patient",
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <ActionIconButton
                    aria-label="Edit Patient"
                    onClick={() => {
                      setEditPatientModal(true);
                      setEditData(record);
                    }}
                  >
                    <EditIcon />
                  </ActionIconButton>
                </span>
              )}
            </Space>
          )}
        </>
      ),
    },
  ];
  return (
    <>
      <div ref={tableWrapRef} className="reception-patient-list-table">
        <Table
          className="no-vertical-borders"
          columns={columns}
          dataSource={data?.data}
          pagination={getTablePagination({
            current: page,
            pageSize: 20,
            total: data?.total_count ?? 0,
            onChange: (p) => setPage(p),
          })}
          scroll={{
            ...tableScroll,
            x: "400px",
          }}
          loading={loading}
        />
      </div>

      {open && (
        <InPatientModal
          open={open}
          setOpen={setOpen}
          editData={editData}
          refresh={refresh}
        />
      )}
      {editPatientModal && (
        <AddPatientModal
          open={editPatientModal}
          setOpen={setEditPatientModal}
          editData={editData}
          refresh={refresh}
        />
      )}
      {addAppointment && (
        <AppointsModal
          open={addAppointment}
          setOpen={setAddAppointment}
          refresh={refresh}
          editData={editData}
        />
      )}
    </>
  );
};

export default AllPatientTable;
